'use client';

import React from 'react';
import {
  X,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  AlertTriangle,
  Loader2 as Spinner,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import type { AppointmentStatus, AppointmentFinancialStatus } from '@/lib/api';
import { usePlatformLabel } from '@/lib/platform-labels';

// ─────────────────────────────────────────────────────────────────────────────
// Badge - usa `.badge` do design system atual.
// API legada do projeto (variant/tone/size) e mapeada pras classes oficiais.
// ─────────────────────────────────────────────────────────────────────────────

export type BadgeVariant = 'filled' | 'subtle' | 'outline';

export type BadgeTone =
  | 'default'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'done'
  | 'neutral'
  | 'admin';

export type BadgeSize = 'sm' | 'md' | 'lg';
export type BadgeShape = 'pill' | 'rounded' | 'square';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  tone?: BadgeTone;
  size?: BadgeSize;
  shape?: BadgeShape;
  leftIcon?: React.ReactNode;
  dot?: boolean;
  onDismiss?: React.MouseEventHandler<HTMLButtonElement>;
  dismissLabel?: string;
}

const SIZE_CLASS: Record<BadgeSize, string> = {
  sm: 'badge--sm',
  md: '',
  lg: 'badge--lg',
};

const SHAPE_CLASS: Record<BadgeShape, string> = {
  pill: 'badge--pill',
  rounded: 'badge--rounded',
  square: 'badge--square',
};

const SOFT_TONE_CLASS: Record<BadgeTone, string> = {
  default: 'badge--neutral',
  info: 'badge--info',
  success: 'badge--success',
  warning: 'badge--warning',
  danger: 'badge--error',
  done: 'badge--admin',
  neutral: 'badge--neutral',
  admin: 'badge--admin',
};

const SOLID_TONE_CLASS: Record<BadgeTone, string> = {
  default: 'badge--neutral',
  info: 'badge--solid-info',
  success: 'badge--solid-success',
  warning: 'badge--solid-warning',
  danger: 'badge--solid-error',
  done: 'badge--admin',
  neutral: 'badge--neutral',
  admin: 'badge--admin',
};

function getBadgeClasses(variant: BadgeVariant, tone: BadgeTone) {
  return cn(
    variant === 'filled' ? SOLID_TONE_CLASS[tone] : SOFT_TONE_CLASS[tone],
    variant === 'outline' && 'badge--outline',
  );
}

export function Badge({
  className,
  variant = 'subtle',
  tone = 'neutral',
  size = 'md',
  shape = 'pill',
  leftIcon,
  dot,
  onDismiss,
  dismissLabel = 'Remover filtro',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'badge',
        getBadgeClasses(variant, tone),
        SIZE_CLASS[size],
        SHAPE_CLASS[shape],
        onDismiss && 'badge--dismissible',
        className,
      )}
      {...props}
    >
      {dot && <span className="badge__dot" aria-hidden="true" />}
      {leftIcon && (
        <span className="badge__icon" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      {children}
      {onDismiss && (
        <button
          type="button"
          className="badge__remove"
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <X aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status badges. Labels/tones canonicos em @/lib/status-labels - ponto
// unico de verdade. Aqui orquestramos UI + usePlatformLabel pra permitir
// customizacao via PlatformMessage no DB.
// ─────────────────────────────────────────────────────────────────────────────

import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_TONES,
  APPOINTMENT_STATUS_VARIANTS,
  FINANCIAL_STATUS_LABELS,
} from '@/lib/status-labels';
import { financialBadgeTone } from '@/lib/financial-status';

export function StatusBadge({
  status,
  className,
  size,
  variant,
}: {
  status: AppointmentStatus;
  className?: string;
  size?: BadgeSize;
  variant?: BadgeVariant;
}) {
  const label = usePlatformLabel(
    `status.appointment.${status}`,
    APPOINTMENT_STATUS_LABELS[status],
  );
  return (
    <Badge
      variant={variant ?? APPOINTMENT_STATUS_VARIANTS[status]}
      tone={APPOINTMENT_STATUS_TONES[status]}
      size={size}
      dot
      className={className}
    >
      {label}
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FinancialStatusBadge - selo da decisao financeira de um Appointment.
// Ortogonal ao StatusBadge (operacional). Em telas com foco financeiro
// (/dashboard/financial), os dois selos aparecem lado a lado para evidenciar
// que status='COMPLETED' e financialStatus podem variar independentemente.
// Doc: docs/08-quality-audits/status-integrity-audit.md
// ─────────────────────────────────────────────────────────────────────────────

export function FinancialStatusBadge({
  status,
  className,
  size,
  variant = 'subtle',
}: {
  status: AppointmentFinancialStatus;
  className?: string;
  size?: BadgeSize;
  variant?: BadgeVariant;
}) {
  const label = usePlatformLabel(
    `status.financial.${status}`,
    FINANCIAL_STATUS_LABELS[status],
  );
  return (
    <Badge
      variant={variant}
      tone={financialBadgeTone(status)}
      size={size}
      dot
      className={className}
    >
      {label}
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Variacoes especializadas - cada uma reusa o Badge base com props especificas.
// Todas sao read-only (sem botao de fechar) por design - quando precisar do
// X, use <Badge onDismiss=...>.
// ─────────────────────────────────────────────────────────────────────────────

/** Badge para strings tecnicas (keys, IDs, slugs) - monospace + warning suave
 *  square por padrao, replicando a variacao "Criado há 30d" do catalogo de
 *  filtros removiveis mas sem o botao de fechar. Garante destaque visual em
 *  metadados tecnicos. Tom e shape podem ser sobrescritos. */
export function CodeBadge({
  children,
  size = 'sm',
  title,
  tone = 'warning',
  shape = 'square',
  className,
}: {
  children: React.ReactNode;
  size?: BadgeSize;
  title?: string;
  tone?: BadgeTone;
  shape?: BadgeShape;
  className?: string;
}) {
  return (
    <Badge
      variant="subtle"
      tone={tone}
      size={size}
      shape={shape}
      className={cn('badge--code', className)}
      title={title}
    >
      <code style={{ fontFamily: 'var(--font-mono)' }}>{children}</code>
    </Badge>
  );
}

/** Badge contador - "12 novos", "3 pendentes". Numero em destaque + label. */
export function CounterBadge({
  count,
  label,
  tone = 'info',
  size = 'sm',
}: {
  count: number;
  label?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge variant="subtle" tone={tone} size={size}>
      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</strong>
      {label && <span style={{ marginLeft: 4 }}>{label}</span>}
    </Badge>
  );
}

/** Badge temporal - "há 30d", "agora", "ontem". Inclui prefixo opcional.
 *  shape="square" por padrao replicando o visual "Criado há 30d" do catalogo. */
export function AgeBadge({
  label,
  prefix,
  tone = 'neutral',
  size = 'sm',
  shape = 'square',
}: {
  label: string;
  prefix?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
  shape?: BadgeShape;
}) {
  return (
    <Badge variant="subtle" tone={tone} size={size} shape={shape}>
      {prefix ? `${prefix} ${label}` : label}
    </Badge>
  );
}

/** Badge com dot animado - status real-time (Online, Conectando, Ao vivo). */
export function PulseBadge({
  children,
  tone = 'success',
  size = 'sm',
}: {
  children: React.ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge
      variant="subtle"
      tone={tone}
      size={size}
      className="badge--pulse"
      dot
    >
      {children}
    </Badge>
  );
}

/** Badge de prioridade - Alta/Média/Baixa com cor coerente. */
export function PriorityBadge({
  level,
  size = 'sm',
}: {
  level: 'high' | 'medium' | 'low';
  size?: BadgeSize;
}) {
  const LABELS = { high: 'Alta', medium: 'Média', low: 'Baixa' } as const;
  const TONES: Record<typeof level, BadgeTone> = {
    high: 'danger',
    medium: 'warning',
    low: 'info',
  };
  return (
    <Badge variant="subtle" tone={TONES[level]} size={size} dot>
      {LABELS[level]}
    </Badge>
  );
}

/** Badge monetario - "R$ 1.234,56" com formatacao tabular. */
export function CurrencyBadge({
  amount,
  currency = 'BRL',
  tone = 'neutral',
  size = 'sm',
}: {
  amount: number;
  currency?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
  return (
    <Badge variant="subtle" tone={tone} size={size}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatted}</span>
    </Badge>
  );
}

/** Badge de percentual - "+12.5%" ou "-3%". signed adiciona sinal. */
export function PercentBadge({
  value,
  signed = false,
  tone,
  size = 'sm',
}: {
  value: number;
  signed?: boolean;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  const sign = signed && value > 0 ? '+' : '';
  const inferredTone: BadgeTone =
    tone ?? (value > 0 ? 'success' : value < 0 ? 'danger' : 'neutral');
  return (
    <Badge variant="subtle" tone={inferredTone} size={size}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {sign}
        {value.toFixed(1)}%
      </span>
    </Badge>
  );
}

/** Badge de tendencia - seta direcional + valor. Up/down/flat. */
export function TrendBadge({
  value,
  direction,
  size = 'sm',
}: {
  value: string | number;
  direction: 'up' | 'down' | 'flat';
  size?: BadgeSize;
}) {
  const ICONS = {
    up: <ArrowUpRight size={12} aria-hidden="true" />,
    down: <ArrowDownRight size={12} aria-hidden="true" />,
    flat: <Minus size={12} aria-hidden="true" />,
  } as const;
  const TONES: Record<typeof direction, BadgeTone> = {
    up: 'success',
    down: 'danger',
    flat: 'neutral',
  };
  return (
    <Badge
      variant="subtle"
      tone={TONES[direction]}
      size={size}
      leftIcon={ICONS[direction]}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </Badge>
  );
}

/** Badge com avatar - iniciais ou letra de identificacao. */
export function AvatarBadge({
  name,
  initials,
  tone = 'info',
  size = 'sm',
}: {
  name: string;
  initials?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  const computed = initials ?? name.slice(0, 2).toUpperCase();
  return (
    <Badge variant="subtle" tone={tone} size={size} shape="rounded">
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: 'var(--brand-soft)',
          color: 'var(--brand-700)',
          fontSize: 9,
          fontWeight: 700,
          marginRight: 4,
        }}
        aria-hidden="true"
      >
        {computed}
      </span>
      {name}
    </Badge>
  );
}

/** Badge chave: valor - "ID: abc123", "Plano: Pro". Chave em peso normal, valor em destaque. */
export function KeyValueBadge({
  label,
  value,
  tone = 'neutral',
  size = 'sm',
}: {
  label: string;
  value: React.ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge variant="subtle" tone={tone} size={size}>
      <span style={{ opacity: 0.7 }}>{label}:</span>
      <strong style={{ marginLeft: 4 }}>{value}</strong>
    </Badge>
  );
}

/** Badge de grupo - "+3 outros", quando ha N itens nao mostrados. */
export function GroupBadge({
  count,
  prefix = '+',
  suffix = 'outros',
  tone = 'neutral',
  size = 'sm',
}: {
  count: number;
  prefix?: string;
  suffix?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  if (count <= 0) return null;
  return (
    <Badge variant="outline" tone={tone} size={size} shape="rounded">
      {prefix}
      {count} {suffix}
    </Badge>
  );
}

/** Badge para estado vazio - "-" ou "Sem valor". Cinza sutil. */
export function EmptyBadge({
  label = '-',
  size = 'sm',
}: {
  label?: string;
  size?: BadgeSize;
}) {
  return (
    <Badge
      variant="outline"
      tone="neutral"
      size={size}
      shape="rounded"
      style={{ opacity: 0.6 }}
    >
      {label}
    </Badge>
  );
}

/** Badge skeleton - placeholder durante carregamento, com shimmer. */
export function LoadingBadge({ size = 'sm' }: { size?: BadgeSize }) {
  return (
    <Badge
      variant="subtle"
      tone="neutral"
      size={size}
      leftIcon={<Spinner size={11} className="animate-spin" aria-hidden="true" />}
    >
      Carregando...
    </Badge>
  );
}

/** Badge de aviso/atencao - reforco visual + icone de alerta. */
export function AlertBadge({
  children,
  tone = 'warning',
  size = 'sm',
}: {
  children: React.ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge
      variant="subtle"
      tone={tone}
      size={size}
      leftIcon={<AlertTriangle size={11} aria-hidden="true" />}
    >
      {children}
    </Badge>
  );
}

/** Badge "Novo" - chama atencao para um item recem-criado/adicionado. */
export function NewMarkerBadge({ size = 'sm' }: { size?: BadgeSize }) {
  return (
    <Badge
      variant="filled"
      tone="info"
      size={size}
      shape="rounded"
      className="badge--new"
    >
      Novo
    </Badge>
  );
}

/** Badge apenas dot - indicador visual minimo (5px) sem texto.
 *  Use em listas densas onde label sobrecarregaria. Cor codifica o estado. */
export function DotBadge({
  tone = 'success',
  ariaLabel,
}: {
  tone?: BadgeTone;
  ariaLabel: string;
}) {
  const COLORS: Record<BadgeTone, string> = {
    default: 'var(--text-3)',
    info: 'var(--brand-500)',
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger: 'var(--error)',
    done: 'var(--text-3)',
    neutral: 'var(--text-3)',
    admin: 'var(--admin-500)',
  };
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: COLORS[tone],
      }}
    />
  );
}

/** Badge de intervalo numerico - "12 / 50", "R$ 1k-5k". Util para metas/quotas. */
export function RangeBadge({
  current,
  total,
  unit,
  tone,
  size = 'sm',
}: {
  current: number | string;
  total: number | string;
  unit?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge variant="subtle" tone={tone ?? 'info'} size={size}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        <strong>{current}</strong>
        <span style={{ opacity: 0.6, margin: '0 4px' }}>/</span>
        {total}
        {unit && <span style={{ opacity: 0.6, marginLeft: 2 }}>{unit}</span>}
      </span>
    </Badge>
  );
}

/** Badge de versao - "v1.2.3", monospace tabular. Para releases/builds. */
export function VersionBadge({
  version,
  prefix = 'v',
  tone = 'neutral',
  size = 'sm',
}: {
  version: string;
  prefix?: string;
  tone?: BadgeTone;
  size?: BadgeSize;
}) {
  return (
    <Badge variant="outline" tone={tone} size={size} shape="rounded">
      <code
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {prefix}
        {version}
      </code>
    </Badge>
  );
}
